/**
 * postLoginTarget — the SINGLE source of truth for where a user lands after a
 * successful login (or when an already-authenticated user hits /login).
 *
 * Path-A login warm-up (Stream D): Tracker-home companies route through the
 * `/welcome` warm-up splash instead of straight to `/portal`, so the dashboard
 * has fresh snapshots by the time the user arrives. Classic/filing companies
 * are UNAFFECTED — they keep going to `/buildings` (or the ?redirect target).
 *
 * This used to be an inline `platformHome === "portal" ? "/portal" : "/buildings"`
 * ternary duplicated across THREE call sites (LoginFiling, Login legacy,
 * __root). With three copies, a race or a missed edit meant the splash would
 * flicker or be skipped entirely. Centralizing here keeps all entry points in
 * lockstep (feedback_dry_across_surfaces: same decision, one function).
 *
 * Tracker-ONLY, fail-toward-no-splash: ONLY `platformHome === "portal"` sends a
 * user to `/welcome`. Anything else — Classic, unset, or a missing company —
 * falls through to the dashboard. A non-Tracker user must NEVER see the splash
 * or trigger a warm-up.
 *
 * NOTE the camelCase axios boundary (api/base.ts deep-camelizes every /me
 * response key): we read `company.platformHome`, not `platform_home`.
 */
import type { UserType } from "@/api/user";

/**
 * The shape we hand to TanStack Router's `navigate(...)`. We keep it loose
 * (`to: string` + optional `search`) rather than reaching for the generated
 * route-tree union — the three call sites already pass plain strings here and
 * the router accepts them at runtime.
 */
export type PostLoginNavigateArg = {
  to: string;
  search?: Record<string, string>;
};

/** A same-origin path, else undefined (p2_deep_link, Leo OK 10-21): one leading "/", no "\" and no control character.
 * Trap: dot segments resolve /..//evil.example to the path //evil.example, so the resolved path is checked too. */
export const safeRedirect = (v: unknown): string | undefined =>
  typeof v === "string" &&
  v.startsWith("/") &&
  !v.startsWith("//") &&
  ![...v].some((c) => c === "\\" || c < " " || c === "\x7f") &&
  !new URL(v, "http://x").pathname.startsWith("//")
    ? v
    : undefined;

/** The signed-out bounce (root and PortalLayout): the page and its search ride ?redirect, so ?it= and mt survive login. */
export const loginRedirect = (
  pathname: string,
  search: string,
): PostLoginNavigateArg => ({
  to: "/login",
  search: { redirect: pathname + search },
});

/** A ?redirect as a navigation, else `fallback`. router-core takes the path from `to` and the search only from
 * `search`, so they are split. Trap: never navigate({href}); router-core reloads any absolute href, an open redirect. */
export const redirectTo = (
  redirect: unknown,
  fallback: string,
): PostLoginNavigateArg => {
  const safe = safeRedirect(redirect);
  if (!safe) return { to: fallback };
  const url = new URL(safe, "http://x");
  const search = Object.fromEntries(url.searchParams);
  return Object.keys(search).length
    ? { to: url.pathname, search }
    : { to: url.pathname };
};

/**
 * Where should this user go right after authenticating?
 *
 * @param user     the freshly-refetched /me payload (camelized)
 * @param redirect an optional ?redirect target captured on the /login URL
 *                 (e.g. a deep link the user hit while logged out)
 *
 * Tracker-home  -> `/welcome` (warm-up splash), carrying ?redirect through so
 *                 the splash can hand the user to their intended deep link once
 *                 the workspace is ready.
 * Everyone else -> the ?redirect target if present, else `/buildings`.
 */
export function postLoginTarget(
  user: UserType | null | undefined,
  redirect?: string,
): PostLoginNavigateArg {
  const isPortalHome = user?.company?.platformHome === "portal";
  if (isPortalHome) {
    // A Tracker-home user's home is /portal, never the Classic root ("/").
    // Drop a ?redirect that points back at Classic root so the /welcome
    // hand-off lands on /portal instead of flashing the Classic dashboard
    // (the accidental-landing path: logged-out portal user hits "/" ->
    // /login?redirect=/ -> /welcome -> exit to "/"). The __root guard also
    // catches this, but sanitizing here avoids the Classic flash mid-hand-off.
    const carried = safeRedirect(redirect);
    return {
      to: "/welcome",
      ...(carried && carried !== "/" ? { search: { redirect: carried } } : {}),
    };
  }
  return redirectTo(redirect, "/buildings");
}

/**
 * Should a Tracker-home user who is currently on the Classic root ("/") be
 * bounced to /portal? True for the *accidental* landings (bookmark, typed root
 * URL, restored tab, boot auth-race /login?redirect=/, a ?redirect=/ carried
 * through /welcome) and false for a *deliberate* switch into Classic.
 *
 * A deliberate switch always carries the ?fromPortal=1 hand-off flag, which
 * usePortalReturnMode mirrors to sessionStorage as `portalReturnMode`. We key
 * ONLY on that flag — not the full usePortalReturnMode signal, whose
 * `inheritedFromCompany` branch is true for any portal user on any Classic
 * surface and would neuter the guard. Staff are never bounced: "/" is their
 * real home and the "Filing View" escape hatch must keep working.
 *
 * Pure + side-effect-free so __root.tsx's redirect effect and the unit test
 * share one decision (feedback_dry_across_surfaces).
 */
export function shouldRedirectTrackerHomeToPortal(args: {
  pathname: string;
  search?: Record<string, unknown> | null;
  user: UserType | null | undefined;
  /** sessionStorage.getItem("portalReturnMode"); pass null when unavailable. */
  portalReturnFlag: string | null;
}): boolean {
  const { pathname, search, user, portalReturnFlag } = args;
  if (pathname !== "/") return false;
  if (!user) return false;
  if (user.company?.platformHome !== "portal") return false;
  if (user.isStaff) return false;
  const s = (search ?? {}) as Record<string, unknown>;
  const explicitClassicSwitch =
    s.fromPortal === "1" ||
    s.fromPortal === 1 ||
    portalReturnFlag === "1";
  return !explicitClassicSwitch;
}

/**
 * The navigation the root owes this render, or null; first match wins. The root renders nothing while
 * one is due, so no page paints before a bounce (prod 10-14: Classic flashed before /portal).
 */
export function rootRedirect(args: {
  pathname: string;
  /** location.searchStr: raw, with its "?". */
  searchStr?: string;
  search?: Record<string, unknown> | null;
  user: UserType | null | undefined;
  isSuccess: boolean;
  isUnauthRoute: boolean;
  /** sessionStorage.getItem("portalReturnMode"); pass null when unavailable. */
  portalReturnFlag: string | null;
}): PostLoginNavigateArg | null {
  const { pathname, searchStr = "", search, user, isSuccess, isUnauthRoute, portalReturnFlag } = args;
  if (!isSuccess) return null;
  if (!user) return isUnauthRoute ? null : loginRedirect(pathname, searchStr);
  // The form's own target: a /me refetch on /login (a focus refetch) must not drop the ?redirect.
  if (pathname === "/login")
    return postLoginTarget(user, safeRedirect(search?.redirect));
  // Portal gate: API access is the tracking_beta group; FE access is either signal.
  const portalHome = user.company?.platformHome === "portal";
  if (pathname.startsWith("/portal") && !user.trackingEnabled && !portalHome) return { to: "/" };
  if (shouldRedirectTrackerHomeToPortal({ pathname, search, user, portalReturnFlag })) {
    return { to: "/portal" };
  }
  return null;
}
